import { getChatCompletion } from '@/lib/ai/chatCompletion';
import type { WizardData, OutfitData } from './types';

const ITEM_ACCENT_COLORS = [
  '#C9A96E', // gold
  '#7BA05B', // sage green
  '#5B8FA0', // slate blue
  '#9B6E8A', // mauve
  '#C07B5A', // terracotta
  '#6B8FA0', // steel blue
  '#A09B6E', // olive gold
];

export interface StyleProfile {
  aesthetics: string[];
  occasions: string[];
  brandAffinities: string[];
  sustainableOnly: boolean;
  preOwnedPreferred: boolean;
}

export interface SavedOutfitSummary {
  aesthetic: string;
  keyWords: string[];
  occasion?: string;
}

export function buildGoogleShoppingUrl(itemName: string, category: string): string {
  const query = encodeURIComponent(`${itemName} ${category}`);
  return `https://www.google.com/search?tbm=shop&q=${query}`;
}

function buildPrompt(data: WizardData, styleProfile?: StyleProfile | null, savedHistory?: SavedOutfitSummary[]): string {
  const appearanceDesc = data.appearance.mode === 'details'
    ? `Skin tone: ${data.appearance.skinTone}, Body shape: ${data.appearance.bodyShape}, Height: ${data.appearance.height}, Hair colour: ${data.appearance.hairColour}`
    : `Appearance description from photo: ${data.appearance.photoDescription}`;

  // Build personalisation context from style profile
  let personalisationBlock = '';
  if (styleProfile) {
    const parts: string[] = [];
    if (styleProfile.aesthetics.length > 0) {
      parts.push(`Preferred aesthetics: ${styleProfile.aesthetics.join(', ')}`);
    }
    if (styleProfile.occasions.length > 0) {
      parts.push(`Go-to occasions: ${styleProfile.occasions.join(', ')}`);
    }
    if (styleProfile.brandAffinities.length > 0) {
      parts.push(`Brand affinities: ${styleProfile.brandAffinities.join(', ')}`);
    }
    if (styleProfile.sustainableOnly) {
      parts.push('Strong preference: sustainable and B Corp certified brands only');
    }
    if (styleProfile.preOwnedPreferred) {
      parts.push('Strong preference: prioritise pre-owned and secondhand platforms');
    }
    if (parts.length > 0) {
      personalisationBlock = `\nUser Style Profile (weight these heavily in your recommendations):\n${parts.map(p => `- ${p}`).join('\n')}\n`;
    }
  }

  // Build saved outfit history context
  let historyBlock = '';
  if (savedHistory && savedHistory.length > 0) {
    const aestheticCounts: Record<string, number> = {};
    const keywordCounts: Record<string, number> = {};
    for (const s of savedHistory) {
      if (s.aesthetic) {
        aestheticCounts[s.aesthetic] = (aestheticCounts[s.aesthetic] ?? 0) + 1;
      }
      for (const kw of s.keyWords ?? []) {
        keywordCounts[kw] = (keywordCounts[kw] ?? 0) + 1;
      }
    }
    const topAesthetics = Object.entries(aestheticCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([a]) => a);
    const topKeywords = Object.entries(keywordCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([k]) => k);

    const historyParts: string[] = [];
    if (topAesthetics.length > 0) {
      historyParts.push(`Recurring aesthetics from saved outfits: ${topAesthetics.join(', ')}`);
    }
    if (topKeywords.length > 0) {
      historyParts.push(`Recurring style keywords from saved outfits: ${topKeywords.join(', ')}`);
    }
    if (historyParts.length > 0) {
      historyBlock = `\nLearned from saved outfit history (use to sharpen recommendations):\n${historyParts.map(p => `- ${p}`).join('\n')}\n`;
    }
  }

  return `You are an elite fashion stylist creating an editorial outfit recommendation.

Context:
- Occasion: ${data.occasion}
- Budget: ${data.currency} ${data.budget} (total for the complete outfit)
- Gender: ${data.gender}
- ${appearanceDesc}
${personalisationBlock}${historyBlock}
Create a complete, cohesive outfit with 5-7 individual items. Each item must be specific (not generic), with realistic pricing that fits within the total budget.

For the "Shop the Look" recommendations, actively mix sources to include:
- Pre-owned / secondhand platforms such as Vestiaire Collective, Depop, ThredUp, The RealReal, or Vinted
- B Corp certified or sustainable brands (e.g. Patagonia, Eileen Fisher, Stella McCartney, Reformation, Thought Clothing)
- Traditional retailers where appropriate

Where possible, suggest at least 1-2 items from pre-owned platforms or B Corp / sustainable brands. Reflect the source in the item name or styling note (e.g. "via Vestiaire Collective" or "Reformation (B Corp)").

Respond ONLY with valid JSON in this exact format:
{
  "editorialTitle": "A short evocative editorial headline (5-8 words, no quotes)",
  "editorialSubtitle": "One sentence describing the overall aesthetic and vibe of this look",
  "aesthetic": "2-3 word aesthetic label (e.g. 'Quiet Luxury', 'Urban Edge')",
  "stylistNotes": "2-3 sentences of professional styling advice for this specific look, body type, and occasion",
  "keyWords": ["word1", "word2", "word3", "word4", "word5"],
  "items": [
    {
      "id": "item-001",
      "name": "Specific product name with brand-level detail",
      "category": "Category name",
      "estimatedPrice": "$XX",
      "stylingNote": "One sentence on why this piece works and how to wear it",
      "searchQuery": "Google Shopping search query for this item"
    }
  ]
}`;
}

// Mock outfit data for fallback on API error
function getMockOutfit(data: WizardData): OutfitData {
  const currencySymbol = data.currency === 'EUR' ? '€'
    : data.currency === 'GBP' ? '£'
    : data.currency === 'CAD' ? 'C$'
    : data.currency === 'AUD'? 'A$' :'$';

  const isWomen = data.gender === 'women';
  const budget = data.budget;

  const occasionLower = data.occasion.toLowerCase();
  const isFormal = occasionLower.includes('black tie') || occasionLower.includes('gala') || occasionLower.includes('wedding');
  const isCasual = occasionLower.includes('brunch') || occasionLower.includes('casual') || occasionLower.includes('festival') || occasionLower.includes('weekend');
  const isWork = occasionLower.includes('interview') || occasionLower.includes('business') || occasionLower.includes('office');

  const mockSets: Record<string, OutfitData> = {
    women_formal: {
      editorialTitle: 'After Midnight in Ivory',
      editorialSubtitle: 'A study in restraint — column silhouettes and cool metallic accents that command any room.',
      aesthetic: 'Quiet Opulence',
      stylistNotes: `For a ${data.occasion}, the key is elongated lines and tonal dressing. An ivory column gown creates an unbroken vertical line that photographs beautifully under evening lighting. Keep accessories architectural and minimal — let one statement earring do the work of an entire jewellery collection. Nude satin heels extend the leg and avoid breaking the column silhouette.`,
      keyWords: ['Column silhouette', 'Tonal dressing', 'Understated glamour', 'Evening elegance', 'Architectural accessories'],
      items: [
        { id: 'item-001', name: 'Ivory Crepe Column Midi Gown', category: 'Dress', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.38)}`, stylingNote: 'The hero piece — a bias-cut column gown in ivory creates effortless evening elegance without trying too hard.', searchQuery: 'ivory crepe column midi evening gown', accentColor: '#C9A96E' },
        { id: 'item-002', name: 'Satin Pointed-Toe Mule Heels', category: 'Shoes', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.22)}`, stylingNote: 'Barely-there nude satin mules extend the leg line and keep the focus on the gown.', searchQuery: 'satin pointed toe mule heels nude', accentColor: '#9B6E8A' },
        { id: 'item-003', name: 'Long Sculptural Drop Earrings', category: 'Jewellery', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.12)}`, stylingNote: 'A single statement earring at the ear replaces the need for a necklace — cleaner and more editorial.', searchQuery: 'sculptural gold drop earrings evening', accentColor: '#7BA05B' },
        { id: 'item-004', name: 'Compact Satin Minaudière Clutch', category: 'Bag', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.16)}`, stylingNote: 'A structured satin clutch in champagne keeps the palette cohesive and the silhouette uncluttered.', searchQuery: 'satin minaudière clutch champagne evening bag', accentColor: '#C07B5A' },
        { id: 'item-005', name: 'Sheer Silk Wrap Stole', category: 'Accessory', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.08)}`, stylingNote: 'A lightweight silk stole draped over the shoulders adds warmth without disrupting the evening aesthetic.', searchQuery: 'silk wrap stole ivory evening shawl', accentColor: '#5B8FA0' },
      ],
    },
    women_casual: {
      editorialTitle: 'Sunday Edit, Effortlessly Yours',
      editorialSubtitle: 'The art of looking put-together while feeling like you threw it on in five minutes.',
      aesthetic: 'Relaxed Chic',
      stylistNotes: `For ${data.occasion}, the winning formula is elevated basics with one textural statement. Linen wide-leg trousers and a fitted ribbed top create an easy proportion that works on most body shapes. A quality leather tote does the heavy lifting on polish — invest here over fast fashion. Flat sandals or clean white trainers complete the look depending on the weather.`,
      keyWords: ['Elevated basics', 'Easy proportions', 'Textural contrast', 'Effortless polish', 'Relaxed silhouette'],
      items: [
        { id: 'item-001', name: 'Ivory Linen Wide-Leg Trousers', category: 'Trousers', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.28)}`, stylingNote: 'Wide-leg linen is the chicest way to stay cool — the volume at the hem balances any top choice.', searchQuery: 'ivory linen wide leg trousers women', accentColor: '#C9A96E' },
        { id: 'item-002', name: 'Ribbed Cropped Cotton Knit Top', category: 'Top', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.16)}`, stylingNote: 'A fitted rib knit in ecru creates a clean torso and lets the trouser silhouette breathe.', searchQuery: 'ribbed cropped knit top women ecru', accentColor: '#7BA05B' },
        { id: 'item-003', name: 'Tan Leather Structured Tote', category: 'Bag', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.32)}`, stylingNote: 'A well-structured leather tote in cognac tan anchors the whole look in quality — worth the spend.', searchQuery: 'tan leather structured tote bag women', accentColor: '#C07B5A' },
        { id: 'item-004', name: 'Leather Slide Sandals with Buckle', category: 'Shoes', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.18)}`, stylingNote: 'A single-strap leather slide with a minimal buckle keeps the foot light and the aesthetic elevated.', searchQuery: 'leather slide sandals buckle women minimalist', accentColor: '#5B8FA0' },
        { id: 'item-005', name: 'Demi-Fine Gold Chain Bracelet', category: 'Jewellery', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.06)}`, stylingNote: 'A delicate layered chain bracelet adds subtle warmth without effort.', searchQuery: 'demi fine gold chain bracelet women', accentColor: '#9B6E8A' },
      ],
    },
    men_formal: {
      editorialTitle: 'The Architecture of Black Tie',
      editorialSubtitle: 'Precision tailoring and tonal depth — a man who understands that fit is everything.',
      aesthetic: 'Sharp Formalism',
      stylistNotes: `For ${data.occasion}, the goal is a silhouette with no excess fabric. A midnight navy tuxedo reads more sophisticated than plain black in most evening lighting. The shirt should be slim — no billowing at the torso. Invest in the shoes and pocket square; they carry the personality in an otherwise strict dress code.`,
      keyWords: ['Sharp tailoring', 'Tonal depth', 'Slim silhouette', 'Evening formalism', 'Considered details'],
      items: [
        { id: 'item-001', name: 'Midnight Navy Slim-Cut Tuxedo Jacket', category: 'Jacket', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.35)}`, stylingNote: 'Midnight navy reads more nuanced than black under evening lighting — choose slim lapels for a modern cut.', searchQuery: 'midnight navy slim tuxedo jacket men', accentColor: '#5B8FA0' },
        { id: 'item-002', name: 'Matching Tuxedo Trousers with Satin Stripe', category: 'Trousers', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.2)}`, stylingNote: 'The satin side stripe is non-negotiable for black tie — break the look without it and you have a suit.', searchQuery: 'tuxedo trousers satin stripe navy men', accentColor: '#9B6E8A' },
        { id: 'item-003', name: 'Slim Poplin Tuxedo Shirt', category: 'Shirt', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.12)}`, stylingNote: 'A slim poplin shirt with a bib front and french cuffs keeps the torso clean and formal.', searchQuery: 'slim poplin tuxedo shirt bib front men', accentColor: '#C9A96E' },
        { id: 'item-004', name: 'Black Patent Oxford Shoes', category: 'Shoes', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.22)}`, stylingNote: 'Patent leather oxfords are the correct choice — nothing else reflects light the same way under evening chandeliers.', searchQuery: 'black patent leather oxford shoes men formal', accentColor: '#C07B5A' },
        { id: 'item-005', name: 'Ivory Silk Pocket Square', category: 'Accessory', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.04)}`, stylingNote: 'A single fold of ivory silk in the breast pocket adds personality within the strict dress code.', searchQuery: 'ivory silk pocket square men', accentColor: '#7BA05B' },
        { id: 'item-006', name: 'Silk Grosgrain Bow Tie', category: 'Tie', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.07)}`, stylingNote: 'A self-tied grosgrain bow tie signals effort and mastery — pre-tied ones read as costume.', searchQuery: 'silk grosgrain bow tie black men self tie', accentColor: '#C9A96E' },
      ],
    },
    men_casual: {
      editorialTitle: 'The Effortless Edit',
      editorialSubtitle: 'Considered casualwear where every piece earns its place — nothing excess, nothing missing.',
      aesthetic: 'Elevated Casual',
      stylistNotes: `For ${data.occasion}, the key is tonal dressing with textural contrast. Olive or slate chinos in a slim-straight cut work across almost every body shape and create a clean base for layering. A quality Oxford shirt left untucked with the sleeves rolled to mid-forearm is the most versatile move in menswear. A suede loafer bridges the gap between effort and ease perfectly.`,
      keyWords: ['Tonal palette', 'Textural layering', 'Proportional balance', 'Smart casual', 'Effortless polish'],
      items: [
        { id: 'item-001', name: 'Slim-Straight Chino Trousers in Slate', category: 'Trousers', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.22)}`, stylingNote: 'Slate chinos in a slim-straight cut are the most versatile trouser in a man\'s wardrobe — clean and contemporary.', searchQuery: 'slim straight chino trousers slate grey men', accentColor: '#5B8FA0' },
        { id: 'item-002', name: 'Oxford Cloth Button-Down Shirt in Ecru', category: 'Shirt', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.18)}`, stylingNote: 'An OCBD in ecru worn untucked with sleeves rolled to the mid-forearm is the backbone of smart casual dressing.', searchQuery: 'oxford cloth button down shirt ecru men', accentColor: '#C9A96E' },
        { id: 'item-003', name: 'Tan Suede Penny Loafer', category: 'Shoes', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.32)}`, stylingNote: 'Suede penny loafers worn without socks in warmer months strike the perfect balance between relaxed and refined.', searchQuery: 'tan suede penny loafer men', accentColor: '#C07B5A' },
        { id: 'item-004', name: 'Merino Wool Crew-Neck Knit', category: 'Top', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.2)}`, stylingNote: 'A lightweight merino crew in camel or navy layers cleanly over the shirt for textural depth.', searchQuery: 'merino wool crew neck knit men camel', accentColor: '#7BA05B' },
        { id: 'item-005', name: 'Leather Minimal Strap Watch', category: 'Watch', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.08)}`, stylingNote: 'A clean watch with a leather strap adds intention without overcomplicating the look.', searchQuery: 'minimal leather strap watch men dress', accentColor: '#9B6E8A' },
      ],
    },
    men_work: {
      editorialTitle: 'The Confident Candidate',
      editorialSubtitle: 'Precision over polish — dressed to be taken seriously before you\'ve said a word.',
      aesthetic: 'Power Casual',
      stylistNotes: `For ${data.occasion}, the goal is looking confident, not overdressed. A well-fitted navy or charcoal suit in a lighter weight fabric reads professional without being intimidating. The shirt should be immaculate — white or light blue only. A leather Derby or Oxford in dark brown creates warmth against the suit.`,
      keyWords: ['Professional authority', 'Immaculate fit', 'Confident restraint', 'Interview-ready', 'First impression'],
      items: [
        { id: 'item-001', name: 'Charcoal Slim-Fit Suit Jacket', category: 'Blazer', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.32)}`, stylingNote: 'Charcoal is the safest and most authoritative suit colour for interviews — it reads serious without being severe.', searchQuery: 'charcoal slim fit suit jacket men interview', accentColor: '#5B8FA0' },
        { id: 'item-002', name: 'Matching Slim Suit Trousers', category: 'Trousers', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.18)}`, stylingNote: 'Matched trousers keep the silhouette clean and intentional — never mix a suit jacket with odd trousers for an interview.', searchQuery: 'charcoal slim suit trousers men', accentColor: '#9B6E8A' },
        { id: 'item-003', name: 'White Poplin Dress Shirt', category: 'Shirt', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.12)}`, stylingNote: 'A white poplin shirt with a straight collar is non-negotiable — it photographs well and signals precision.', searchQuery: 'white poplin dress shirt men slim fit', accentColor: '#C9A96E' },
        { id: 'item-004', name: 'Dark Burgundy Silk Tie', category: 'Tie', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.08)}`, stylingNote: 'A burgundy tie adds personality within the professional palette — avoid novelty prints entirely.', searchQuery: 'dark burgundy silk tie men', accentColor: '#C07B5A' },
        { id: 'item-005', name: 'Dark Brown Leather Derby Shoes', category: 'Shoes', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.25)}`, stylingNote: 'Dark brown Derbies with a charcoal suit is a classic combination that signals taste without ostentation.', searchQuery: 'dark brown leather derby shoes men formal', accentColor: '#7BA05B' },
        { id: 'item-006', name: 'Leather Slim Bifold Wallet', category: 'Accessory', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.05)}`, stylingNote: 'A slim leather wallet in matching brown keeps the pocket profile flat under tailored trousers.', searchQuery: 'slim leather bifold wallet dark brown men', accentColor: '#C9A96E' },
      ],
    },
    women_work: {
      editorialTitle: 'The Power in Subtlety',
      editorialSubtitle: 'Dressed to lead — where precision tailoring and considered details do the talking.',
      aesthetic: 'Corporate Edge',
      stylistNotes: `For ${data.occasion}, authority comes from fit, not formality. A blazer in a neutral — camel, navy, or charcoal — worn over tailored trousers creates a silhouette that commands attention. Keep colour in one accent piece only. Pointed-toe kitten heels or clean leather loafers signal professionalism while keeping comfort intact for a full day.`,
      keyWords: ['Tailored authority', 'Neutral palette', 'Considered details', 'Professional confidence', 'Precision fit'],
      items: [
        { id: 'item-001', name: 'Camel Double-Breasted Blazer', category: 'Blazer', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.32)}`, stylingNote: 'A double-breasted camel blazer is the most authoritative piece in professional womenswear — wear it buttoned.', searchQuery: 'camel double breasted blazer women tailored', accentColor: '#C9A96E' },
        { id: 'item-002', name: 'High-Waist Straight-Leg Trousers in Ivory', category: 'Trousers', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.22)}`, stylingNote: 'Ivory trousers against a camel blazer creates a tonal look that reads more editorial than a full matching suit.', searchQuery: 'high waist straight leg trousers ivory women', accentColor: '#7BA05B' },
        { id: 'item-003', name: 'Silk Blouse in Pale Champagne', category: 'Top', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.18)}`, stylingNote: 'A silk blouse tucked into high-waist trousers adds softness to the tailored structure above.', searchQuery: 'silk blouse champagne pale women work', accentColor: '#9B6E8A' },
        { id: 'item-004', name: 'Pointed-Toe Kitten Heel Mule', category: 'Shoes', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.2)}`, stylingNote: 'A kitten heel in nude or camel extends the leg line and keeps comfort viable through a full work day.', searchQuery: 'pointed toe kitten heel mule women nude', accentColor: '#C07B5A' },
        { id: 'item-005', name: 'Structured Mini Top-Handle Bag', category: 'Bag', estimatedPrice: `${currencySymbol}${Math.round(budget * 0.08)}`, stylingNote: 'A compact top-handle bag in tan leather signals intention and keeps the silhouette from being overwhelmed.', searchQuery: 'structured mini top handle bag tan leather women', accentColor: '#5B8FA0' },
      ],
    },
  };

  const occasionLowerCheck = data.occasion.toLowerCase();
  const isFormalCheck = occasionLowerCheck.includes('black tie') || occasionLowerCheck.includes('gala') || occasionLowerCheck.includes('wedding');
  const isWorkCheck = occasionLowerCheck.includes('interview') || occasionLowerCheck.includes('business') || occasionLowerCheck.includes('office');

  let key: string;
  if (isWomen && isFormalCheck) key = 'women_formal';
  else if (isWomen && isWorkCheck) key = 'women_work';
  else if (isWomen) key = 'women_casual';
  else if (!isWomen && isFormalCheck) key = 'men_formal';
  else if (!isWomen && isWorkCheck) key = 'men_work';
  else key = 'men_casual';

  const result = mockSets[key];

  return {
    ...result,
    items: result.items.map((item, i) => ({
      ...item,
      accentColor: item.accentColor ?? ITEM_ACCENT_COLORS[i % ITEM_ACCENT_COLORS.length],
    })),
  };
}

export function getMockOutfitFallback(data: WizardData): OutfitData {
  return getMockOutfit(data);
}

export async function generateOutfit(data: WizardData, styleProfile?: StyleProfile | null, savedHistory?: SavedOutfitSummary[]): Promise<OutfitData> {
  try {
    const prompt = buildPrompt(data, styleProfile, savedHistory);

    const response = await getChatCompletion(
      'GEMINI',
      'gemini/gemini-2.5-flash',
      [
        {
          role: 'system',
          content: 'You are an elite fashion stylist. Always respond with valid JSON only — no markdown, no code fences, no extra text.',
        },
        {
          role: 'user',
          content: prompt,
        },
      ],
      {
        temperature: 0.8,
        max_tokens: 2048,
      }
    );

    const text = response?.choices?.[0]?.message?.content ?? '';

    // Extract JSON from response (strip any accidental markdown fences)
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) throw new Error('No JSON found in Gemini response');

    const parsed = JSON.parse(jsonMatch[0]) as OutfitData;

    return {
      ...parsed,
      items: parsed.items.map((item, i) => ({
        ...item,
        id: item.id ?? `item-${String(i + 1).padStart(3, '0')}`,
        accentColor: ITEM_ACCENT_COLORS[i % ITEM_ACCENT_COLORS.length],
      })),
    };
  } catch (error) {
    console.error('Gemini outfit generation failed, using fallback:', error);
    // Fallback to mock data on error
    await new Promise(resolve => setTimeout(resolve, 1500));
    return getMockOutfit(data);
  }
}